import { useEffect, useState } from 'react';
import { authFetch } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * useContent(sectionKey)
 *
 * Fetches CMS content for a section (e.g. "hero", "features", "cta", "footer")
 * from /api/settings/{key} and returns:
 *
 *   pick(field)  →  returns the value for current language, falling back to EN.
 *
 * Components pass a `staticFallback` object so the UI always renders even if
 * the backend is unreachable or the section has not been seeded yet.
 */
export const useContent = (sectionKey, staticFallback = {}) => {
  const { language } = useLanguage();
  const [content, setContent] = useState(staticFallback);

  useEffect(() => {
    let active = true;
    authFetch(`/api/settings/${sectionKey}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!active) return;
        if (data && data.value && typeof data.value === 'object') {
          // Merge over fallback so partial overrides still work
          setContent({ ...staticFallback, ...data.value });
        }
      })
      .catch(() => { /* keep fallback */ });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey]);

  const pick = (field) => content[`${field}_${language}`] ?? content[`${field}_en`] ?? '';

  return { content, pick };
};
